import React, { useState, useEffect, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card, Modal, ModalBody } from "reactstrap";
import {
  Table,
  Empty,
  Tag,
  Select,
  Input,
  Descriptions,
  Statistic,
  Card as AntCard,
  DatePicker,
  InputNumber,
  Tabs,
  Tooltip,
  Drawer,
  Badge as AntBadge,
} from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import {
  EyeOutlined,
  DollarOutlined,
  BankOutlined,
  LockOutlined,
  UnlockOutlined,
  BellOutlined,
  BarChartOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const paymentMethodOptions = [
  { label: "Bank Transfer", value: "BankTransfer" },
  { label: "Card", value: "Card" },
  { label: "Cash", value: "Cash" },
  { label: "Cheque", value: "Cheque" },
  { label: "NEFT", value: "NEFT" },
  { label: "RTGS", value: "RTGS" },
  { label: "IMPS", value: "IMPS" },
  { label: "UPI", value: "UPI" },
  { label: "Other", value: "Other" },
];

const formatCurrency = (amount: any) => {
  if (amount === null || amount === undefined) return "-";
  return `₹${Number(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ReceivablesList: React.FC = () => {
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);
  const [activeView, setActiveView] = useState<"dashboard" | "aging" | "pending">("dashboard");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
  });

  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Aging report
  const [agingData, setAgingData] = useState<any[]>([]);
  const [agingLoading, setAgingLoading] = useState(false);

  // Pending payments
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Filters
  const [filterBillingStatus, setFilterBillingStatus] = useState<string | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Modals
  const [detailModal, setDetailModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerDetail, setCustomerDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [paymentModal, setPaymentModal] = useState(false);
  const [advanceModal, setAdvanceModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline form visibility inside detail modal
  const [showInlinePayment, setShowInlinePayment] = useState(false);
  const [showInlineAdvance, setShowInlineAdvance] = useState(false);

  // Bulk selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // Payment form
  const [payCustomerId, setPayCustomerId] = useState<number | undefined>(undefined);
  const [payCustomerName, setPayCustomerName] = useState("");
  const [payInvoiceId, setPayInvoiceId] = useState<number | undefined>(undefined);
  const [payDate, setPayDate] = useState<any>(null);
  const [payAmount, setPayAmount] = useState<number | undefined>(undefined);
  const [payMethod, setPayMethod] = useState<string | undefined>(undefined);
  const [payReference, setPayReference] = useState("");
  const [payBankName, setPayBankName] = useState("");
  const [payRemarks, setPayRemarks] = useState("");

  // Advance form
  const [advCustomerId, setAdvCustomerId] = useState<number | undefined>(undefined);
  const [advCustomerName, setAdvCustomerName] = useState("");
  const [advDate, setAdvDate] = useState<any>(null);
  const [advAmount, setAdvAmount] = useState<number | undefined>(undefined);
  const [advMethod, setAdvMethod] = useState<string | undefined>(undefined);
  const [advReference, setAdvReference] = useState("");
  const [advBankName, setAdvBankName] = useState("");
  const [advRemarks, setAdvRemarks] = useState("");

  const buildFilters = useCallback(() => {
    const filters: any = {};
    if (filterBillingStatus) filters.status = filterBillingStatus;
    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [filterBillingStatus]);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const filters = params.filters !== undefined ? params.filters : buildFilters();
      const pageNumber = params.current || pagination.current;
      const pageSize = params.pageSize || pagination.pageSize;
      const apiParams: any = {
        pageNumber,
        pageSize,
        sortOrder: params.sortOrder || pagination.sortOrder,
        globalSearch: params.searchQuery !== undefined ? params.searchQuery : onSearchText,
      };

      if (filters) {
        if (filters.status) apiParams["filters[billingStatus]"] = filters.status;
      }

      const { data, status, message } = await superAdminService.getReceivablesDashboard(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.data || data?.list || [];
        const count = data?.totalRecords || (Array.isArray(data) ? data.length : data?.count || 0);
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          current: pageNumber,
          pageSize,
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgingReport = async () => {
    setAgingLoading(true);
    try {
      const { data, status, message } = await superAdminService.getAgingReport({
        sortOrder: "DESC",
        direction: "P",
        pageSize: 100,
        pageDiff: 0,
      });
      if (status) {
        const list = Array.isArray(data) ? data : data?.data || data?.list || [];
        setAgingData(list);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setAgingLoading(false);
    }
  };

  const fetchPendingPayments = async () => {
    setPendingLoading(true);
    try {
      const { data, status, message } = await superAdminService.getPendingPayments({
        sortOrder: "DESC",
        direction: "P",
        pageSize: 100,
        pageDiff: 0,
      });
      if (status) {
        const list = Array.isArray(data) ? data : data?.data || data?.list || [];
        setPendingPayments(list);
        setPendingCount(list.length);
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApprovePayment = async (paymentId: number) => {
    Swal.fire({
      title: "Approve Payment?",
      text: "This will mark the payment as approved and update the invoice balance.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, approve",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: { confirmButton: "btn btn-success me-2", cancelButton: "btn btn-secondary" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.approvePayment({ paymentId });
          if (res.status) {
            Swal.fire("Approved!", "Payment has been approved.", "success");
            fetchPendingPayments();
          } else {
            Swal.fire("Error", res.message || "Failed to approve", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleRejectPayment = async (paymentId: number) => {
    Swal.fire({
      title: "Reject Payment?",
      text: "This will reject the payment. The customer will need to resubmit.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reject",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: { confirmButton: "btn btn-danger me-2", cancelButton: "btn btn-secondary" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.rejectPayment({ paymentId });
          if (res.status) {
            Swal.fire("Rejected!", "Payment has been rejected.", "success");
            fetchPendingPayments();
          } else {
            Swal.fire("Error", res.message || "Failed to reject", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({ current: 1 });
    // Fetch pending count in background for badge
    fetchPendingPayments();
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({ current: 1, searchQuery: debouncedSearch.trim() });
  }, [debouncedSearch]);

  const tableOnChange = async (newPagination: any) => {
    await fetchData({ current: newPagination.current, pageSize: newPagination.pageSize });
  };

  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    fetchData({ current: 1 });
  }, [filterBillingStatus]);

  const refreshList = () => {
    hasFetched.current = false;
    fetchData({ current: 1 });
  };

  // --- View Customer Detail ---
  const openDetailModal = async (record: any) => {
    setSelectedCustomer(record);
    setDetailModal(true);
    setDetailLoading(true);
    setShowInlinePayment(false);
    setShowInlineAdvance(false);
    try {
      const res = await superAdminService.getCustomerReceivableDetail(record.customerId);
      if (res.status && res.data) {
        setCustomerDetail(res.data);
      } else {
        Swal.fire("Error", res.message || "Details not found", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshCustomerDetail = async () => {
    if (!selectedCustomer?.customerId) return;
    try {
      const res = await superAdminService.getCustomerReceivableDetail(selectedCustomer.customerId);
      if (res.status && res.data) setCustomerDetail(res.data);
    } catch {}
  };

  // --- Hold / Unhold ---
  const handleHold = async (customerId: number, customerName: string) => {
    const result = await Swal.fire({
      title: `Hold ${customerName}?`,
      text: "This will block all customer users from accessing the system.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, hold",
      confirmButtonColor: "#d33",
      input: "text",
      inputLabel: "Reason",
      inputPlaceholder: "Enter hold reason",
      inputValidator: (value) => {
        if (!value) return "Reason is required";
        return null;
      },
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.holdCustomer({ customerId, reason: result.value });
      if (res.status) {
        Swal.fire("Success", "Customer put on hold", "success");
        refreshList();
        if (detailModal) setDetailModal(false);
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleUnhold = async (customerId: number, customerName: string) => {
    const result = await Swal.fire({
      title: `Unhold ${customerName}?`,
      text: "This will restore access for all customer users.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, unhold",
      input: "text",
      inputLabel: "Reason (optional)",
      inputPlaceholder: "Enter reason",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.unholdCustomer({ customerId, reason: result.value || undefined });
      if (res.status) {
        Swal.fire("Success", "Customer access restored", "success");
        refreshList();
        if (detailModal) setDetailModal(false);
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // --- Bulk Hold / Unhold ---
  const handleBulkHold = async () => {
    if (selectedRowKeys.length === 0) return;
    const result = await Swal.fire({
      title: `Hold ${selectedRowKeys.length} customer(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, hold all",
      confirmButtonColor: "#d33",
      input: "text",
      inputLabel: "Reason",
      inputPlaceholder: "Enter hold reason",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.bulkHoldCustomers({ customerIds: selectedRowKeys, reason: result.value || undefined });
      if (res.status) {
        Swal.fire("Success", "Customers put on hold", "success");
        setSelectedRowKeys([]);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  const handleBulkUnhold = async () => {
    if (selectedRowKeys.length === 0) return;
    const result = await Swal.fire({
      title: `Unhold ${selectedRowKeys.length} customer(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, unhold all",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await superAdminService.bulkUnholdCustomers({ customerIds: selectedRowKeys, reason: "Bulk unhold" });
      if (res.status) {
        Swal.fire("Success", "Customers access restored", "success");
        setSelectedRowKeys([]);
        refreshList();
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // --- Send Reminder ---
  const handleSendReminder = async (customerId: number) => {
    try {
      const res = await superAdminService.sendPaymentReminder({ customerId });
      if (res.status) {
        Swal.fire("Success", "Payment reminder sent", "success");
      } else {
        Swal.fire("Error", res.message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    }
  };

  // --- Record Payment ---
  const openPaymentModal = (customerId: number, customerName: string) => {
    setPayCustomerId(customerId);
    setPayCustomerName(customerName);
    setPayInvoiceId(undefined);
    setPayDate(null);
    setPayAmount(undefined);
    setPayMethod(undefined);
    setPayReference("");
    setPayBankName("");
    setPayRemarks("");
    setPaymentModal(true);
  };

  const handleRecordPayment = async () => {
    if (!payCustomerId || !payInvoiceId || !payDate || !payAmount || !payMethod || !payReference) return;
    setSaving(true);
    try {
      const res = await superAdminService.recordPayment({
        customerId: payCustomerId,
        invoiceId: payInvoiceId,
        paymentDate: payDate.format("YYYY-MM-DD"),
        amount: payAmount,
        method: payMethod,
        referenceNumber: payReference,
        bankName: payBankName || undefined,
        remarks: payRemarks || undefined,
      });
      if (res.status) {
        Swal.fire("Success", "Payment recorded successfully", "success");
        setPaymentModal(false);
        setShowInlinePayment(false);
        refreshList();
        if (detailModal && selectedCustomer) {
          openDetailModal(selectedCustomer);
        }
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Record Advance ---
  const openAdvanceModal = (customerId: number, customerName: string) => {
    setAdvCustomerId(customerId);
    setAdvCustomerName(customerName);
    setAdvDate(null);
    setAdvAmount(undefined);
    setAdvMethod(undefined);
    setAdvReference("");
    setAdvBankName("");
    setAdvRemarks("");
    setAdvanceModal(true);
  };

  const handleRecordAdvance = async () => {
    if (!advCustomerId || !advDate || !advAmount || !advMethod || !advReference) return;
    setSaving(true);
    try {
      const res = await superAdminService.recordAdvancePayment({
        customerId: advCustomerId,
        paymentDate: advDate.format("YYYY-MM-DD"),
        amount: advAmount,
        method: advMethod,
        referenceNumber: advReference,
        bankName: advBankName || undefined,
        remarks: advRemarks || undefined,
      });
      if (res.status) {
        Swal.fire("Success", "Advance payment recorded successfully", "success");
        setAdvanceModal(false);
        setShowInlineAdvance(false);
        refreshList();
        if (detailModal && selectedCustomer) {
          openDetailModal(selectedCustomer);
        }
      } else {
        const msg = Array.isArray(res.message) ? res.message.join(", ") : res.message;
        Swal.fire("Error", msg, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error?.response?.data?.message || error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  // --- Dashboard Table Columns ---
  const dashboardColumns = [
    {
      title: "Customer",
      dataIndex: "customerName",
      key: "customerName",
      width: 180,
      render: (text: string, record: any) => (
        <a href="#view" className="text-primary fw-bold"
          onClick={(e) => { e.preventDefault(); openDetailModal(record); }}>
          {text || "-"}
        </a>
      ),
    },
    {
      title: "Invoices",
      dataIndex: "totalInvoices",
      key: "totalInvoices",
      width: 80,
      render: (val: number) => val ?? 0,
    },
    {
      title: "Billed",
      dataIndex: "totalBilled",
      key: "totalBilled",
      width: 120,
      render: (val: any) => <span style={{ fontWeight: 600 }}>{formatCurrency(val)}</span>,
    },
    {
      title: "Paid",
      dataIndex: "totalPaid",
      key: "totalPaid",
      width: 120,
      render: (val: any) => <span style={{ color: "#52c41a" }}>{formatCurrency(val)}</span>,
    },
    {
      title: "Advance",
      dataIndex: "advanceBalance",
      key: "advanceBalance",
      width: 110,
      render: (val: any) => formatCurrency(val),
    },
    {
      title: "Outstanding",
      dataIndex: "outstanding",
      key: "outstanding",
      width: 120,
      render: (val: any) => (
        <span style={{ fontWeight: 600, color: Number(val) > 0 ? "#fa8c16" : "#52c41a" }}>
          {formatCurrency(val)}
        </span>
      ),
    },
    {
      title: "Overdue",
      dataIndex: "overdue",
      key: "overdue",
      width: 110,
      render: (val: any) => (
        <span style={{ color: Number(val) > 0 ? "#ff4d4f" : undefined }}>
          {formatCurrency(val)}
        </span>
      ),
    },
    {
      title: "Pending",
      dataIndex: "pendingPayments",
      key: "pendingPayments",
      width: 90,
      render: (val: number) =>
        val > 0 ? (
          <Tag color="orange">{val}</Tag>
        ) : (
          <span style={{ color: "#8091a7" }}>0</span>
        ),
    },
    {
      title: "Oldest Due",
      dataIndex: "oldestDueDate",
      key: "oldestDueDate",
      width: 110,
      render: (date: string) => date ? moment(date).format("DD MMM YY") : "-",
    },
    {
      title: "Status",
      dataIndex: "billingStatus",
      key: "billingStatus",
      width: 110,
      render: (status: string, record: any) => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Tag color={status === "Active" ? "green" : status === "Hold" ? "red" : "default"}>{status || "-"}</Tag>
          {status === "Hold" && record.holdReason && (
            <Tooltip title={record.holdReason}>
              <InfoCircleOutlined style={{ color: "#ff4d4f", fontSize: 13, cursor: "pointer" }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_: any, record: any) => (
        <div className="d-flex" style={{ gap: 4 }}>
          <a href="#view" className="btn btn-sm btn-icon btn-trigger" title="View Details"
            onClick={(e) => { e.preventDefault(); openDetailModal(record); }}>
            <Icon name="eye" />
          </a>
          {record.billingStatus === "Hold" ? (
            <a href="#unhold" className="btn btn-sm btn-icon btn-trigger text-success" title="Unhold"
              onClick={(e) => { e.preventDefault(); handleUnhold(record.customerId, record.customerName); }}>
              <Icon name="unlock" />
            </a>
          ) : (
            <a href="#hold" className="btn btn-sm btn-icon btn-trigger text-danger" title="Hold"
              onClick={(e) => { e.preventDefault(); handleHold(record.customerId, record.customerName); }}>
              <Icon name="lock" />
            </a>
          )}
          <a href="#reminder" className="btn btn-sm btn-icon btn-trigger text-warning" title="Send Reminder"
            onClick={(e) => { e.preventDefault(); handleSendReminder(record.customerId); }}>
            <Icon name="notify" />
          </a>
        </div>
      ),
    },
  ];

  // --- Aging Report Columns ---
  const agingColumns = [
    { title: "Customer", dataIndex: "customerName", key: "customerName", width: 180 },
    { title: "Current", dataIndex: "current", key: "current", width: 110, render: (v: any) => formatCurrency(v) },
    { title: "0-30 Days", dataIndex: "days0_30", key: "days0_30", width: 110, render: (v: any) => formatCurrency(v) },
    { title: "31-60 Days", dataIndex: "days31_60", key: "days31_60", width: 110, render: (v: any) => formatCurrency(v) },
    { title: "61-90 Days", dataIndex: "days61_90", key: "days61_90", width: 110, render: (v: any) => formatCurrency(v) },
    {
      title: "90+ Days",
      dataIndex: "days90Plus",
      key: "days90Plus",
      width: 110,
      render: (v: any) => <span style={{ color: Number(v) > 0 ? "#ff4d4f" : undefined, fontWeight: Number(v) > 0 ? 600 : undefined }}>{formatCurrency(v)}</span>,
    },
    {
      title: "Total Outstanding",
      dataIndex: "totalOutstanding",
      key: "totalOutstanding",
      width: 130,
      render: (v: any) => <strong>{formatCurrency(v)}</strong>,
    },
  ];

  const hasActiveFilters = !!filterBillingStatus;
  const activeFilterCount = [filterBillingStatus].filter(Boolean).length;

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };

  return (
    <React.Fragment>
      <Head title="Receivables Management" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Receivables Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>Customer-wise receivables overview.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                {selectedRowKeys.length > 0 && (
                  <>
                    <li>
                      <Button color="danger" className="btn-dim" onClick={handleBulkHold}>
                        <Icon name="lock"></Icon>
                        <span>Hold ({selectedRowKeys.length})</span>
                      </Button>
                    </li>
                    <li>
                      <Button color="success" className="btn-dim" onClick={handleBulkUnhold}>
                        <Icon name="unlock"></Icon>
                        <span>Unhold ({selectedRowKeys.length})</span>
                      </Button>
                    </li>
                  </>
                )}
                <li>
                  <Button
                    color={activeView === "aging" ? "primary" : "secondary"}
                    className="btn-dim"
                    onClick={() => {
                      if (activeView === "aging") {
                        setActiveView("dashboard");
                      } else {
                        setActiveView("aging");
                        fetchAgingReport();
                      }
                    }}
                  >
                    <Icon name={activeView === "aging" ? "dashboard" : "bar-chart"}></Icon>
                    <span>{activeView === "aging" ? "Dashboard" : "Aging Report"}</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color={activeView === "pending" ? "primary" : "warning"}
                    className="btn-dim"
                    onClick={() => {
                      if (activeView === "pending") {
                        setActiveView("dashboard");
                      } else {
                        setActiveView("pending");
                        fetchPendingPayments();
                      }
                    }}
                  >
                    <Icon name={activeView === "pending" ? "dashboard" : "check-circle"}></Icon>
                    <span>{activeView === "pending" ? "Dashboard" : `Pending Approvals${pendingCount > 0 ? ` (${pendingCount})` : ""}`}</span>
                  </Button>
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            {activeView === "pending" ? (
              /* Pending Payments Approval View */
              <div className="card-inner p-0">
                <div className="card-inner py-3 border-bottom d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <CheckCircleOutlined style={{ marginRight: 8 }} />
                    Pending Payment Approvals ({pendingPayments.length})
                  </h6>
                  <Button className="btn-icon btn-trigger" onClick={fetchPendingPayments}>
                    <ReloadOutlined />
                  </Button>
                </div>
                {pendingLoading ? (
                  <LoadingSpinner />
                ) : pendingPayments.length > 0 ? (
                  <Table
                    columns={[
                      {
                        title: "Customer",
                        dataIndex: "customerName",
                        key: "customerName",
                        render: (text: string) => <strong>{text || "-"}</strong>,
                      },
                      {
                        title: "Invoice #",
                        dataIndex: "invoiceNumber",
                        key: "invoiceNumber",
                        render: (text: string) => text || "-",
                      },
                      {
                        title: "Amount",
                        dataIndex: "amount",
                        key: "amount",
                        render: (val: any) => <strong style={{ color: "#1890ff" }}>{formatCurrency(val)}</strong>,
                      },
                      {
                        title: "Method",
                        dataIndex: "method",
                        key: "method",
                        render: (text: string) => <Tag>{text}</Tag>,
                      },
                      {
                        title: "Reference #",
                        dataIndex: "referenceNumber",
                        key: "referenceNumber",
                        render: (text: string) => text || "-",
                      },
                      {
                        title: "Payment Date",
                        dataIndex: "paymentDate",
                        key: "paymentDate",
                        render: (date: string) => date ? moment(date).format("DD MMM YYYY") : "-",
                      },
                      {
                        title: "Submitted",
                        dataIndex: "createdAt",
                        key: "createdAt",
                        render: (date: string) => date ? moment(date).format("DD MMM YY, hh:mm A") : "-",
                      },
                      {
                        title: "Action",
                        key: "action",
                        width: 160,
                        render: (_: any, record: any) => (
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button color="success" size="sm" onClick={() => handleApprovePayment(record.paymentId)}>
                              <Icon name="check"></Icon> <span>Approve</span>
                            </Button>
                            <Button color="danger" size="sm" className="btn-dim" onClick={() => handleRejectPayment(record.paymentId)}>
                              <Icon name="cross"></Icon>
                            </Button>
                          </div>
                        ),
                      },
                    ]}
                    dataSource={pendingPayments}
                    rowKey="paymentId"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1000 }}
                    bordered
                    size="middle"
                  />
                ) : (
                  <div style={{ padding: 40 }}>
                    <Empty description="No pending payments to approve" />
                  </div>
                )}
              </div>
            ) : activeView === "dashboard" ? (
              <>
                <div className="card-inner py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <Input
                      prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                      placeholder="Search by customer, transaction..."
                      value={onSearchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      variant="borderless"
                      style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                      allowClear
                    />
                    <div className="d-flex align-items-center" style={{ gap: 4 }}>
                      <Tooltip title="Refresh">
                        <span
                          onClick={() => {
                            hasFetched.current = false;
                            fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                          }}
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                            transition: "background 0.2s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                          <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                        </span>
                      </Tooltip>
                      <Tooltip title="Filters">
                        <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                          <span
                            onClick={() => setFilterDrawerOpen(true)}
                            style={{
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                          </span>
                        </AntBadge>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                <div className="card-inner p-0">
                  {loading ? (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                          {["", "Customer", "Invoices", "Billed", "Paid", "Advance", "Outstanding", "Overdue", "Oldest Due", "Status", "Actions"].map((h) => (
                            <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...Array(10)].map((_, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 16, height: 16, borderRadius: 2 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 140, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 40, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 70, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 75, height: 14 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 22, borderRadius: 4 }} /></td>
                            <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : dataSource.length > 0 ? (
                    <Table
                      columns={dashboardColumns}
                      dataSource={dataSource}
                      rowKey="customerId"
                      rowSelection={rowSelection}
                      rowClassName={(record: any) => record.billingStatus === "Hold" ? "receivable-row-hold" : ""}
                      pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                      }}
                      onChange={tableOnChange}
                      scroll={{ x: 1300, y: 500 }}
                      bordered
                      size="small"
                    />
                  ) : (
                    <Empty description="No receivables data found" />
                  )}
                </div>
              </>
            ) : (
              /* Aging Report View */
              <div className="card-inner p-0">
                <div className="card-inner py-3 border-bottom">
                  <h6 className="mb-0">
                    <BarChartOutlined style={{ marginRight: 8 }} />
                    Aging Analysis Report
                  </h6>
                </div>
                {agingLoading ? (
                  <LoadingSpinner />
                ) : agingData.length > 0 ? (
                  <Table
                    columns={agingColumns}
                    dataSource={agingData}
                    rowKey="customerId"
                    pagination={false}
                    scroll={{ x: 900, y: 500 }}
                    bordered
                    size="small"
                  />
                ) : (
                  <Empty description="No aging data available" />
                )}
              </div>
            )}
          </Card>
        </Block>

        {/* Customer Detail Modal */}
        <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="xl" style={{ maxWidth: "1200px" }}>
          <ModalBody>
            {detailLoading ? (
              <LoadingSpinner />
            ) : customerDetail ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="title mb-0">
                    <TeamOutlined style={{ marginRight: 8 }} />
                    {selectedCustomer?.customerName || "Customer"} — Receivables
                  </h5>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button color="primary" size="sm" className="btn-dim"
                      onClick={() => {
                        setShowInlinePayment(!showInlinePayment);
                        setShowInlineAdvance(false);
                        if (!showInlinePayment) {
                          setPayCustomerId(selectedCustomer.customerId);
                          setPayCustomerName(selectedCustomer.customerName);
                          setPayInvoiceId(undefined);
                          setPayDate(null);
                          setPayAmount(undefined);
                          setPayMethod(undefined);
                          setPayReference("");
                          setPayBankName("");
                          setPayRemarks("");
                        }
                      }}>
                      <Icon name="coin" /> <span>Record Payment</span>
                    </Button>
                    <Button color="info" size="sm" className="btn-dim"
                      onClick={() => {
                        setShowInlineAdvance(!showInlineAdvance);
                        setShowInlinePayment(false);
                        if (!showInlineAdvance) {
                          setAdvCustomerId(selectedCustomer.customerId);
                          setAdvCustomerName(selectedCustomer.customerName);
                          setAdvDate(null);
                          setAdvAmount(undefined);
                          setAdvMethod(undefined);
                          setAdvReference("");
                          setAdvBankName("");
                          setAdvRemarks("");
                        }
                      }}>
                      <Icon name="wallet" /> <span>Record Advance</span>
                    </Button>
                    {selectedCustomer?.billingStatus === "Hold" ? (
                      <Button color="success" size="sm" className="btn-dim"
                        onClick={() => handleUnhold(selectedCustomer.customerId, selectedCustomer.customerName)}>
                        <Icon name="unlock" /> <span>Unhold</span>
                      </Button>
                    ) : (
                      <Button color="danger" size="sm" className="btn-dim"
                        onClick={() => handleHold(selectedCustomer.customerId, selectedCustomer.customerName)}>
                        <Icon name="lock" /> <span>Hold</span>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Inline Record Payment Form */}
                {showInlinePayment && (
                  <AntCard size="small" className="mb-4" style={{ borderColor: "#6576ff" }}>
                    <h6 className="mb-3" style={{ fontWeight: 600 }}>
                      <DollarOutlined style={{ marginRight: 8, color: "#6576ff" }} />
                      Record Payment — {payCustomerName}
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Invoice <span className="text-danger">*</span></label>
                        <Select
                          style={{ width: "100%" }}
                          value={payInvoiceId}
                          onChange={(val) => {
                            setPayInvoiceId(val);
                            const inv = (customerDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === val);
                            if (inv && inv.balanceAmount) {
                              setPayAmount(Number(Number(inv.balanceAmount).toFixed(2)));
                            }
                          }}
                          placeholder="Select invoice"
                          showSearch
                          optionFilterProp="label"
                          options={(customerDetail?.invoices || [])
                            .filter((inv: any) => Number(inv.balanceAmount) > 0)
                            .map((inv: any) => ({
                              value: inv.invoiceId || inv.id,
                              label: `${inv.invoiceNumber || "INV-" + (inv.invoiceId || inv.id)} — Bal: ₹${Number(inv.balanceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                            }))}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                        <DatePicker
                          style={{ width: "100%" }}
                          value={payDate}
                          onChange={(val) => setPayDate(val)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Amount <span className="text-danger">*</span></label>
                        <InputNumber
                          style={{ width: "100%" }}
                          value={payAmount}
                          onChange={(val) => setPayAmount(val ?? undefined)}
                          min={0.01}
                          max={(() => {
                            const inv = (customerDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === payInvoiceId);
                            return inv ? Number(Number(inv.balanceAmount).toFixed(2)) : undefined;
                          })()}
                          precision={2}
                          prefix="₹"
                          status={(() => {
                            if (!payInvoiceId || !payAmount) return undefined;
                            const inv = (customerDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === payInvoiceId);
                            if (inv && payAmount > Number(Number(inv.balanceAmount).toFixed(2))) return "error" as const;
                            return undefined;
                          })()}
                        />
                        {payInvoiceId && payAmount && (() => {
                          const inv = (customerDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === payInvoiceId);
                          if (inv && payAmount > Number(Number(inv.balanceAmount).toFixed(2))) {
                            return <div className="text-danger" style={{ fontSize: 12, marginTop: 2 }}>Amount cannot exceed invoice balance (₹{Number(inv.balanceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })})</div>;
                          }
                          return null;
                        })()}
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Method <span className="text-danger">*</span></label>
                        <Select
                          style={{ width: "100%" }}
                          value={payMethod}
                          onChange={(val) => setPayMethod(val)}
                          options={paymentMethodOptions}
                          placeholder="Select method"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Reference No <span className="text-danger">*</span></label>
                        <Input value={payReference} onChange={(e) => setPayReference(e.target.value)} maxLength={100} placeholder="Transaction reference" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Bank Name</label>
                        <Input value={payBankName} onChange={(e) => setPayBankName(e.target.value)} maxLength={100} placeholder="Optional" />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label">Remarks</label>
                        <Input value={payRemarks} onChange={(e) => setPayRemarks(e.target.value)} maxLength={500} placeholder="Optional" />
                      </div>
                    </div>
                    <div className="mt-3" style={{ display: "flex", gap: 8 }}>
                      <Button color="primary" size="sm" onClick={handleRecordPayment}
                        disabled={saving || !payInvoiceId || !payDate || !payAmount || !payMethod || !payReference || (() => {
                          const inv = (customerDetail?.invoices || []).find((i: any) => (i.invoiceId || i.id) === payInvoiceId);
                          return inv ? payAmount > Number(Number(inv.balanceAmount).toFixed(2)) : false;
                        })()}>
                        {saving ? <LoadingSpinner inline size="sm" /> : "Record Payment"}
                      </Button>
                      <Button color="secondary" size="sm" onClick={() => setShowInlinePayment(false)} disabled={saving}>Cancel</Button>
                    </div>
                  </AntCard>
                )}

                {/* Inline Record Advance Form */}
                {showInlineAdvance && (
                  <AntCard size="small" className="mb-4" style={{ borderColor: "#722ed1" }}>
                    <h6 className="mb-3" style={{ fontWeight: 600 }}>
                      <BankOutlined style={{ marginRight: 8, color: "#722ed1" }} />
                      Record Advance Payment — {advCustomerName}
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label">Payment Date <span className="text-danger">*</span></label>
                        <DatePicker
                          style={{ width: "100%" }}
                          value={advDate}
                          onChange={(val) => setAdvDate(val)}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Amount <span className="text-danger">*</span></label>
                        <InputNumber
                          style={{ width: "100%" }}
                          value={advAmount}
                          onChange={(val) => setAdvAmount(val ?? undefined)}
                          min={0.01}
                          precision={2}
                          prefix="₹"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Method <span className="text-danger">*</span></label>
                        <Select
                          style={{ width: "100%" }}
                          value={advMethod}
                          onChange={(val) => setAdvMethod(val)}
                          options={paymentMethodOptions}
                          placeholder="Select method"
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Reference No <span className="text-danger">*</span></label>
                        <Input value={advReference} onChange={(e) => setAdvReference(e.target.value)} maxLength={100} placeholder="Transaction reference" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Bank Name</label>
                        <Input value={advBankName} onChange={(e) => setAdvBankName(e.target.value.replace(/[^a-zA-Z ]/g, ""))} maxLength={100} placeholder="Optional" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Remarks</label>
                        <Input value={advRemarks} onChange={(e) => setAdvRemarks(e.target.value)} maxLength={500} placeholder="Optional" />
                      </div>
                    </div>
                    <div className="mt-3" style={{ display: "flex", gap: 8 }}>
                      <Button color="primary" size="sm" onClick={handleRecordAdvance}
                        disabled={saving || !advDate || !advAmount || !advMethod || !advReference}>
                        {saving ? <LoadingSpinner inline size="sm" /> : "Record Advance"}
                      </Button>
                      <Button color="secondary" size="sm" onClick={() => setShowInlineAdvance(false)} disabled={saving}>Cancel</Button>
                    </div>
                  </AntCard>
                )}

                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-md-3">
                    <AntCard size="small">
                      <Statistic title="Total Billed" value={Number(customerDetail.summary?.totalBilled || 0).toFixed(2)}
                        prefix="₹" valueStyle={{ color: "#1890ff", fontSize: 20 }} />
                    </AntCard>
                  </div>
                  <div className="col-md-3">
                    <AntCard size="small">
                      <Statistic title="Total Paid" value={Number(customerDetail.summary?.totalPaid || 0).toFixed(2)}
                        prefix="₹" valueStyle={{ color: "#52c41a", fontSize: 20 }} />
                    </AntCard>
                  </div>
                  <div className="col-md-3">
                    <AntCard size="small">
                      <Statistic title="Outstanding" value={Math.max(Number(customerDetail.summary?.outstanding || 0), 0).toFixed(2)}
                        prefix="₹" valueStyle={{ color: "#fa8c16", fontSize: 20 }} />
                    </AntCard>
                  </div>
                  <div className="col-md-3">
                    <AntCard size="small">
                      <Statistic title="Advance Balance" value={Number(customerDetail.summary?.advanceBalance || 0).toFixed(2)}
                        prefix="₹" valueStyle={{ color: "#722ed1", fontSize: 20 }} />
                    </AntCard>
                  </div>
                </div>

                <Tabs
                  defaultActiveKey="invoices"
                  items={[
                    {
                      key: "invoices",
                      label: "Invoice-wise Receivables",
                      children: (
                        <Table
                          dataSource={customerDetail.invoices || []}
                          rowKey="invoiceId"
                          pagination={false}
                          scroll={{ x: 900  }}
                          bordered
                          size="small"
                          columns={[
                            { title: "Invoice No", dataIndex: "invoiceNumber", key: "invoiceNumber", width: 130, render: (v: string) => v || "NA" },
                            { title: "Period", dataIndex: "billingPeriod", key: "billingPeriod", width: 100, render: (v: string, r: any) => v || (r.billingMonth ? moment(r.billingMonth).format("MMM-YY") : "-") },
                            { title: "Invoice Amt", dataIndex: "totalAmount", key: "totalAmount", width: 110, render: (v: any) => formatCurrency(v) },
                            { title: "Advance Used", dataIndex: "advanceUsed", key: "advanceUsed", width: 110, render: (v: any) => formatCurrency(v) },
                            { title: "Paid", dataIndex: "paidAmount", key: "paidAmount", width: 100, render: (v: any) => <span style={{ color: "#52c41a" }}>{formatCurrency(v)}</span> },
                            { title: "Balance", dataIndex: "balanceAmount", key: "balanceAmount", width: 100, render: (v: any) => <strong style={{ color: Number(v) > 0 ? "#fa8c16" : undefined }}>{formatCurrency(v)}</strong> },
                            { title: "Due Date", dataIndex: "dueDate", key: "dueDate", width: 100, render: (v: string) => v ? moment(v).format("DD MMM YY") : "-" },
                            {
                              title: "Status", dataIndex: "status", key: "status", width: 100,
                              render: (v: string) => {
                                const colors: Record<string, string> = { Open: "blue", "Partially Paid": "gold", "Fully Paid": "green", Overdue: "red", Cancelled: "default" };
                                return <Tag color={colors[v] || "default"}>{v || "-"}</Tag>;
                              },
                            },
                          ]}
                        />
                      ),
                    },
                    {
                      key: "advances",
                      label: "Advance Payments",
                      children: (
                        <Table
                          dataSource={customerDetail.advances || []}
                          rowKey="advancePaymentId"
                          pagination={false}
                          scroll={{ x: 700  }}
                          bordered
                          size="small"
                          columns={[
                            { title: "Advance Ref", dataIndex: "advanceReferenceNo", key: "advanceReferenceNo", width: 130, render: (v: string) => v || "-" },
                            { title: "Date", dataIndex: "advanceDate", key: "advanceDate", width: 100, render: (v: string) => v ? moment(v).format("DD MMM YY") : "-" },
                            { title: "Amount", dataIndex: "advanceAmount", key: "advanceAmount", width: 110, render: (v: any) => formatCurrency(v) },
                            { title: "Used", dataIndex: "adjustedAmount", key: "adjustedAmount", width: 100, render: (v: any) => formatCurrency(v) },
                            { title: "Balance", dataIndex: "unadjustedBalance", key: "unadjustedBalance", width: 100, render: (v: any) => <strong>{formatCurrency(v)}</strong> },
                            {
                              title: "Status", dataIndex: "status", key: "status", width: 100,
                              render: (v: string) => {
                                const colors: Record<string, string> = { Available: "green", "Partially Used": "gold", "Fully Used": "default", Cancelled: "red" };
                                return <Tag color={colors[v] || "default"}>{v || "-"}</Tag>;
                              },
                            },
                          ]}
                        />
                      ),
                    },
                    {
                      key: "payments",
                      label: "Payment History",
                      children: (
                        <Table
                          dataSource={[...(customerDetail.payments || [])].sort((a: any, b: any) => (a.invoiceNumber || "").localeCompare(b.invoiceNumber || ""))}
                          rowKey="paymentId"
                          pagination={false}
                          scroll={{ x: 1400 }}
                          bordered
                          size="small"
                          columns={[
                            { title: "Payment No", dataIndex: "paymentNumber", key: "paymentNumber", width: 160, fixed: "left" as const, ellipsis: true, render: (v: string) => <span style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{v || "-"}</span> },
                            { title: "Date", dataIndex: "paymentDate", key: "paymentDate", width: 110, ellipsis: true, render: (v: string) => <span style={{ whiteSpace: "nowrap" }}>{v ? moment(v).format("DD MMM YYYY") : "-"}</span> },
                            { title: "Amount", dataIndex: "amount", key: "amount", width: 120, ellipsis: true, render: (v: any) => <strong style={{ whiteSpace: "nowrap" }}>{formatCurrency(v)}</strong> },
                            { title: "Method", dataIndex: "paymentMode", key: "paymentMode", width: 120, ellipsis: true },
                            { title: "Reference", dataIndex: "referenceNumber", key: "referenceNumber", width: 180, ellipsis: true, render: (v: string) => <Tooltip title={v}><span style={{ whiteSpace: "nowrap" }}>{v || "-"}</span></Tooltip> },
                            {
                              title: "Invoice", dataIndex: "invoiceNumber", key: "invoiceNumber", width: 170, ellipsis: true,
                              render: (v: string, record: any) => (
                                <span style={{ whiteSpace: "nowrap" }}>
                                  {v || "Advance"}
                                  {record.invoiceAmount != null && (
                                    <div style={{ fontSize: 11, color: "#8094ae" }}>
                                      Amt: {Number(record.invoiceAmount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </div>
                                  )}
                                </span>
                              ),
                            },
                            {
                              title: "Status", dataIndex: "status", key: "status", width: 100, ellipsis: true,
                              render: (v: string) => {
                                const colors: Record<string, string> = { Cleared: "green", Submitted: "blue", Pending: "gold", Bounced: "red", Cancelled: "default" };
                                return <Tag color={colors[v] || "default"}>{v || "-"}</Tag>;
                              },
                            },
                            {
                              title: "Action", key: "action", width: 180, fixed: "right" as const,
                              render: (_: any, record: any) => {
                                if (record.status === "Pending") {
                                  return (
                                    <div style={{ display: "flex", gap: 6 }}>
                                      <Tooltip title="Approve">
                                        <span style={{ color: "#52c41a", cursor: "pointer", fontSize: 15 }} onClick={async () => {
                                          const result = await Swal.fire({
                                            title: "Approve Payment?",
                                            html: `<b>${record.paymentNumber}</b> — ${formatCurrency(record.amount)}<br/>This will update the invoice as paid.`,
                                            icon: "question", showCancelButton: true, confirmButtonText: "Approve", confirmButtonColor: "#52c41a",
                                          });
                                          if (!result.isConfirmed) return;
                                          try {
                                            const res = await superAdminService.approvePayment({ paymentId: record.paymentId || record.id });
                                            if (res.status) { Swal.fire("Approved!", res.message || "Payment approved.", "success"); refreshCustomerDetail(); fetchData(); }
                                            else Swal.fire("Error", res.message || "Failed", "error");
                                          } catch (e: any) { Swal.fire("Error", e?.response?.data?.message || e.message, "error"); }
                                        }}><CheckCircleOutlined /></span>
                                      </Tooltip>
                                      <Tooltip title="Reject">
                                        <span style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 15 }} onClick={async () => {
                                          const result = await Swal.fire({
                                            title: "Reject Payment?",
                                            html: `<b>${record.paymentNumber}</b> — ${formatCurrency(record.amount)}`,
                                            icon: "warning", input: "text", inputPlaceholder: "Reason (optional)",
                                            showCancelButton: true, confirmButtonText: "Reject", confirmButtonColor: "#ff4d4f",
                                          });
                                          if (!result.isConfirmed) return;
                                          try {
                                            const res = await superAdminService.rejectPayment({ paymentId: record.paymentId || record.id, reason: result.value || undefined });
                                            if (res.status) { Swal.fire("Rejected!", res.message || "Payment rejected.", "success"); refreshCustomerDetail(); }
                                            else Swal.fire("Error", res.message || "Failed", "error");
                                          } catch (e: any) { Swal.fire("Error", e?.response?.data?.message || e.message, "error"); }
                                        }}><StopOutlined /></span>
                                      </Tooltip>
                                    </div>
                                  );
                                }
                                if (record.remarks) {
                                  return <span style={{ fontSize: 12, color: "#8094ae" }}>{record.remarks}</span>;
                                }
                                return null;
                              },
                            },
                          ]}
                        />
                      ),
                    },
                  ]}
                />

                <div className="mt-3 text-end">
                  <Button color="secondary" size="sm" onClick={() => setDetailModal(false)}>Close</Button>
                </div>
              </>
            ) : null}
          </ModalBody>
        </Modal>

        {/* Note: Record Payment and Record Advance forms are now inline inside the detail modal above */}
      </Content>
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
            {activeFilterCount > 0 && (
              <AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />
            )}
          </div>
        }
        placement="right"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={450}
        zIndex={1060}
        extra={
          hasActiveFilters ? (
            <span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={() => setFilterBillingStatus(undefined)}>
              Reset All
            </span>
          ) : null
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{ padding: "14px 16px", borderRadius: 10, background: "linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)", border: "1px solid #d6e4ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Billing Status</span>
            </div>
            <Select
              placeholder="All statuses"
              allowClear
              style={{ width: "100%" }}
              value={filterBillingStatus}
              onChange={(val) => setFilterBillingStatus(val)}
              options={[
                { label: "Active", value: "Active" },
                { label: "Hold", value: "Hold" },
              ]}
            />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default ReceivablesList;
